import { useCallback, useState } from 'react'

/**
 * Claiming a listing.
 *
 * Unlike Regretsit's Council, this always ends in a claim — itch's real PWYW
 * mechanic never blocks $0 — so `unlocked` is always true once the request
 * resolves. The hook still models degraded/error explicitly rather than going
 * quiet on a bad response: the obsidia lesson (silent failure reads as broken,
 * not funny) applies here as much as it did to the rejection case.
 */
export interface NegotiateState {
  pending: boolean
  line: string | null
  unlocked: boolean
  degraded: boolean
  error: string | null
}

export function useNegotiate() {
  const [state, setState] = useState<NegotiateState>({
    pending: false,
    line: null,
    unlocked: false,
    degraded: false,
    error: null,
  })

  const claim = useCallback(async (listingId: string, title: string, suggested: number, offer: number) => {
    setState({ pending: true, line: null, unlocked: false, degraded: false, error: null })
    try {
      const res = await fetch('/api/negotiate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ listingId, title, suggested, offer }),
      })
      const data = (await res.json().catch(() => null)) as
        | { line?: string; unlocked?: boolean; degraded?: boolean }
        | null

      if (!res.ok || !data?.line) {
        setState({
          pending: false,
          line: null,
          unlocked: false,
          degraded: false,
          error: 'The Suggested Price is busy negotiating with someone else. Try again shortly.',
        })
        return
      }

      setState({ pending: false, line: data.line, unlocked: !!data.unlocked, degraded: !!data.degraded, error: null })
    } catch {
      setState({ pending: false, line: null, unlocked: false, degraded: false, error: 'The Suggested Price could not be reached.' })
    }
  }, [])

  const dismiss = useCallback(
    () => setState({ pending: false, line: null, unlocked: false, degraded: false, error: null }),
    [],
  )

  return { ...state, claim, dismiss }
}
